/**
 * Notificações do sistema operacional.
 *
 * A aba do painel costuma ficar em segundo plano — dentro de um iframe, ainda
 * por cima. Sem notificação do sistema, mensagem nova só é percebida quando
 * alguém resolve olhar, o que é tarde.
 *
 * A permissão é pedida a partir de um clique do usuário, nunca no carregamento:
 * pedida sem gesto, a maioria dos navegadores nega de imediato e não pergunta de
 * novo — perder a permissão assim é definitivo.
 */

export type NotificationState = 'unsupported' | 'default' | 'granted' | 'denied';

export function notificationState(): NotificationState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as NotificationState;
}

export async function requestNotificationPermission(): Promise<NotificationState> {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission as NotificationState;

  try {
    return (await Notification.requestPermission()) as NotificationState;
  } catch {
    return 'denied';
  }
}

export interface NotifyOptions {
  title: string;
  body: string;
  /** Notificações com a mesma tag se substituem em vez de empilhar. */
  tag?: string;
  requireInteraction?: boolean;
  onClick?: () => void;
}

export function notify(options: NotifyOptions): boolean {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      tag: options.tag,
      requireInteraction: options.requireInteraction ?? false,
    });

    notification.onclick = () => {
      // Traz o painel para frente: é o que o atendente espera ao clicar.
      window.parent.focus();
      window.focus();
      options.onClick?.();
      notification.close();
    };

    return true;
  } catch {
    // Alguns navegadores exigem service worker para notificações persistentes.
    return false;
  }
}

/**
 * Contador no título da aba. É o aviso que sobrevive a permissão negada e a som
 * desligado, e o que o atendente vê na barra de abas sem trocar de janela.
 */
export function setTitleBadge(count: number, base = 'Atendimento'): void {
  document.title = count > 0 ? `(${count}) ${base}` : base;
}
